import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { UseTheme } from '../../../../common/hooks/useTheme'
import { UseTypeFontSize } from '../../../../common/hooks/useTypeFontSize'
import { Element } from '../../../../common/html/element'
import { getStyle } from '../../../../common/html/helper'
import { Style } from '../../../../common/html/Style'
import { formatVal } from '../../../../helpers/value'
import { MAX_INPUT_LENGTH } from '../constant'
import { isNumeric } from './../helper'
import classes from './index.module.scss'

const regexNumber = /^[+-]?[0-9\b]{0,15}$/
const regexPositiveNumber = /^[0-9\b]{0,15}$/
const regexFloatNumber = /^[+-]?[0-9]{0,7}(\.[0-9]{0,2})?$/

export const Input = ({
  isNumber,
  allowNegativeNumber,
  isFloatNumber,
  value,
  onChange,
  onFocus,
  wrapperStyle,
  wrapperClassname,
  inputStyle,
  prefix,
  suffix,
  disabled,
  noControl,
  autoFocus,
  ...others
}) => {
  const typeFontSize = UseTypeFontSize()
  const theme = UseTheme()
  const locale = useSelector((state) => state.i18n.locale)

  const inputRef = useRef()

  const [inputValue, setInputValue] = useState(value)
  const [borderColor, setBorderColor] = useState('#E0E0E0')

  useEffect(() => {
    handleInpuValueChange()
  }, [inputValue])

  useEffect(() => {
    if (noControl) return
    if (!isFloatNumber) setInputValue(value)
  }, [value, noControl])

  const isFocused = useMemo(() => {
    return document.activeElement === inputRef.current
  }, [document.activeElement, inputRef.current])

  const handleInpuValueChange = () => {
    if (inputValue === value) return
    if (isNumber) {
      if (inputValue === '') onChange('')
      if (isNumeric(inputValue)) onChange(Number(inputValue))
    } else {
      onChange(inputValue)
    }
  }

  const handleSetInputValue = (e) => {
    const value = e.target.value.slice(0, MAX_INPUT_LENGTH)
    if (isNumber) {
      if (
        value === '' ||
        (isFloatNumber
          ? regexFloatNumber
          : allowNegativeNumber
          ? regexNumber
          : regexPositiveNumber
        ).test(value)
      )
        setInputValue(value)
    } else {
      setInputValue(value)
    }
  }

  const handleClick = () => {
    inputRef.current?.focus()
  }

  const onBlur = () => {
    setBorderColor('#E0E0E0')
  }

  const handleFocus = (e) => {
    setBorderColor('#2C73E8')
    e.target.select()
    onFocus && onFocus(e)
  }

  const formatDisplayValue = () => {
    const data = noControl ? value : inputValue
    if (!data || !isNumber) return data
    return isFocused
      ? data
      : formatVal(data, locale, false, isFloatNumber ? 2 : 0)
  }

  return (
    <Element
      className={`${classes.container} ${wrapperClassname}`}
      onClick={handleClick}
      onBlur={onBlur}
      style={{ borderColor, ...wrapperStyle }}
    >
      {prefix}
      <Style
        className={classes.input}
        pseudo="placeholder"
        attr="color"
        color="#9c9c9c"
      >
        <input
          ref={inputRef}
          value={formatDisplayValue()}
          onChange={noControl ? onChange : handleSetInputValue}
          className={classes.input}
          disabled={disabled}
          style={getStyle({ ...inputStyle }, theme, typeFontSize)}
          onFocus={handleFocus}
          autoFocus={autoFocus}
          {...others}
        />
      </Style>
      {suffix}
    </Element>
  )
}

Input.propTypes = {
  isNumber: PropTypes.bool,
  allowNegativeNumber: PropTypes.bool,
  isFloatNumber: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  wrapperClassname: PropTypes.string,
  wrapperStyle: PropTypes.object,
  inputStyle: PropTypes.object,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  disabled: PropTypes.bool,
  noControl: PropTypes.bool,
}

Input.defaultProps = {
  isNumber: false,
  allowNegativeNumber: false,
  isFloatNumber: false,
  value: '',
  onChange: () => {},
  wrapperClassname: '',
  wrapperStyle: {},
  inputStyle: {},
  disabled: false,
  noControl: false,
}
